// The schedule picker behind OrgTriggerModalComponent, free of Angular and service imports so it
// can be unit-tested directly (see org-trigger-template.ts for why that matters here).
//
// TEMPORARY, with the rest of the trigger scaffolding.
//
// The user NEVER sees or types a cron expression. `0 9 * * 1` is unreadable to the person this
// form is for, and the one existing UI that asks for one (trigger-config-modal) also silently
// drops two of its three modes. The pattern copied instead is agent-dashboard.page.ts:1486-1497 -
// a time plus a day preset, assembled into cron here - with a plain-English summary so the choice
// can be checked without knowing the syntax.

export type ScheduleFrequency = 'once' | 'daily' | 'weekdays' | 'weekly' | 'monthly';

export interface ScheduleSpec {
  frequency: ScheduleFrequency;
  // 'HH:MM', 24-hour - the native <input type="time"> value.
  time: string;
  // 0 = Sunday, matching cron's day-of-week field. Only read when frequency is 'weekly'.
  weekdays: number[];
  // 1-31. Only read when frequency is 'monthly'.
  dayOfMonth: number;
  // 'YYYY-MM-DD', the native <input type="date"> value. Only read when frequency is 'once'.
  date: string;
}

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export function blankSchedule(): ScheduleSpec {
  // 09:00 on weekdays: the same working assumption agent-dashboard's default makes, and a
  // schedule that fires during office hours is the safe one to land on by accident.
  return { frequency: 'weekdays', time: '09:00', weekdays: [1], dayOfMonth: 1, date: '' };
}

// A one-time run is NOT a cron. Standard cron has five fields and no year, so the nearest
// expression for "12 August 2026 at 14:00" is `0 14 12 8 *` - which fires every 12 August,
// forever. That is the worst kind of wrong: it looks correct and works the first time. So a
// one-time schedule is carried as a wall-clock timestamp instead, and buildCron returns null.
export function isOneTime(spec: ScheduleSpec): boolean {
  return !!spec && spec.frequency === 'once';
}

// How many minutes the named zone is AHEAD of UTC at that instant. Uses the platform's own tz data
// through Intl, so DST is handled without shipping a database: format the instant in the zone, read
// the wall clock back, and the difference between that and the instant IS the offset.
function zoneOffsetMinutes(zone: string, at: Date): number {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: zone, hour12: false,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit'
  });
  const p: Record<string, string> = {};
  for (const part of dtf.formatToParts(at)) { p[part.type] = part.value; }
  // 'en-US' renders midnight as hour 24 rather than 00, which would push the day forward.
  const hour = p['hour'] === '24' ? 0 : Number(p['hour']);
  const asIfUtc = Date.UTC(
    Number(p['year']), Number(p['month']) - 1, Number(p['day']),
    hour, Number(p['minute']), Number(p['second'])
  );
  return (asIfUtc - at.getTime()) / 60000;
}

function pad2(n: number): string { return String(Math.abs(n)).padStart(2, '0'); }

// The moment a one-time trigger should run, as an OFFSET-QUALIFIED instant:
// '2026-08-14T09:00:00+02:00'.
//
// It used to be the bare wall clock ('2026-08-14T09:00') on the reasoning that `timezone` sits
// beside it and cron is wall-clock anyway. The backend does not read that field: it parsed the
// string as UTC, so a 09:00 Berlin trigger fired at 11:00 Berlin and the only way to get 09:00 was
// to type 07:00. A string carrying its own offset cannot be misread, whichever end reads it.
//
// The offset is resolved AT THE SCHEDULED DATE, not today, so a run booked across a DST boundary
// gets that date's offset (+02:00 in August, +01:00 in January) rather than the current one.
export function buildRunAt(spec: ScheduleSpec, zone?: string): string | null {
  if (!isOneTime(spec) || scheduleError(spec)) { return null; }
  const wall = `${spec.date}T${spec.time}`;
  const timeZone = (zone || '').trim();
  if (!timeZone) { return wall; }   // no zone to resolve against - unchanged rather than guessed
  try {
    // Two passes: the first offset is read at the wall clock treated as UTC, which is up to an hour
    // out near a DST switch; applying it and re-reading settles on the right side of the boundary.
    const naive = Date.parse(`${wall}:00Z`);
    let instant = naive;
    for (let i = 0; i < 2; i++) {
      instant = naive - zoneOffsetMinutes(timeZone, new Date(instant)) * 60000;
    }
    const offset = zoneOffsetMinutes(timeZone, new Date(instant));
    const sign = offset < 0 ? '-' : '+';
    return `${wall}:00${sign}${pad2(Math.trunc(offset / 60))}:${pad2(offset % 60)}`;
  } catch {
    // An unknown zone throws in Intl. The bare wall clock is what shipped before, so fall back to it
    // rather than lose the schedule.
    return wall;
  }
}

// Minutes and hours from 'HH:MM', or null when it is not a time. <input type="time"> yields '' when
// the user clears it, which is the common case here rather than an exotic one.
function parseTime(time: string): { hours: number; minutes: number } | null {
  const match = /^([01]?\d|2[0-3]):([0-5]\d)$/.exec((time || '').trim());
  if (!match) { return null; }
  return { hours: Number(match[1]), minutes: Number(match[2]) };
}

// Why this schedule cannot be saved, or null when it can.
export function scheduleError(spec: ScheduleSpec): string | null {
  if (!spec) { return 'Pick when this should run.'; }
  if (!parseTime(spec.time)) { return 'Pick a time of day.'; }
  if (spec.frequency === 'once' && !/^\d{4}-\d{2}-\d{2}$/.test((spec.date || '').trim())) {
    return 'Pick the date it should run.';
  }
  if (spec.frequency === 'weekly' && !(spec.weekdays || []).length) {
    return 'Pick at least one day of the week.';
  }
  if (spec.frequency === 'monthly') {
    const day = Number(spec.dayOfMonth);
    if (!day || day < 1 || day > 31) { return 'Pick a day of the month between 1 and 31.'; }
  }
  return null;
}

// The cron expression, or null when the spec is not valid yet. Standard 5-field form
// (minute hour day-of-month month day-of-week), which is what chapSchedules already stores.
export function buildCron(spec: ScheduleSpec): string | null {
  if (scheduleError(spec)) { return null; }
  const at = parseTime(spec.time)!;
  const prefix = `${at.minutes} ${at.hours}`;
  switch (spec.frequency) {
    // No cron for a one-time run - see isOneTime. Returning an annual expression here would be the
    // silent every-year bug, so this returns null and the caller stores run_at instead.
    case 'once': return null;
    case 'daily': return `${prefix} * * *`;
    case 'weekdays': return `${prefix} * * 1-5`;
    case 'weekly': {
      // Sorted so two identical selections cannot produce two different expressions - which would
      // otherwise read as a change when re-saving an untouched schedule.
      const days = [...spec.weekdays].sort((a, b) => a - b).join(',');
      return `${prefix} * * ${days}`;
    }
    case 'monthly': return `${prefix} ${Number(spec.dayOfMonth)} * *`;
    default: return null;
  }
}

// The schedule as a sentence, e.g. "Every Monday and Thursday at 09:00". This is the ONLY
// description of the schedule the user gets - the cron string is never shown - so it has to carry
// the whole meaning rather than gloss it.
export function describeSchedule(spec: ScheduleSpec, timezone?: string): string {
  if (scheduleError(spec)) { return ''; }
  const at = spec.time;
  const zone = timezone ? ` (${timezone})` : '';
  switch (spec.frequency) {
    // "Once" is stated explicitly rather than left implied by the date: it is the one frequency
    // that stops afterwards, and that is the fact worth being sure of before saving.
    case 'once': return `Once on ${longDate(spec.date)} at ${at}${zone}`;
    case 'daily': return `Every day at ${at}${zone}`;
    case 'weekdays': return `Every weekday at ${at}${zone}`;
    case 'weekly': {
      const names = [...spec.weekdays].sort((a, b) => a - b).map(d => DAY_NAMES[d]);
      return `Every ${joinWords(names)} at ${at}${zone}`;
    }
    case 'monthly': return `On the ${ordinal(Number(spec.dayOfMonth))} of each month at ${at}${zone}`;
    default: return '';
  }
}

// Months are not all the same length, and cron simply skips a date that does not exist rather than
// falling back to the last day. Said out loud, because "runs monthly" and "runs 7 times a year"
// are very different things to discover afterwards.
export function monthlyGapWarning(spec: ScheduleSpec): string {
  if (!spec || spec.frequency !== 'monthly') { return ''; }
  const day = Number(spec.dayOfMonth);
  if (day === 31) { return 'Only 7 months have a 31st - the others will be skipped.'; }
  if (day === 30) { return 'February has no 30th, so February will be skipped.'; }
  if (day === 29) { return 'February has a 29th only in a leap year.'; }
  return '';
}

// Whether a one-time run is already in the past, given the current wall-clock time as
// 'YYYY-MM-DDTHH:MM'. `now` is passed in rather than read from the clock so this stays pure and
// the spec can pin it.
//
// An approximation on purpose: it compares the schedule's wall clock against the BROWSER's, which
// only strictly agree when the user sits in the schedule's timezone. That is right for the job it
// does - catching "you picked yesterday" before saving - and it is a warning, never a block, so
// being an hour out at the boundary costs nothing.
export function isInThePast(spec: ScheduleSpec, now: string): boolean {
  // NO zone on purpose: buildRunAt returns the bare wall clock without one, which is what makes the
  // lexical comparison below valid. Passing the zone would return '...+02:00' and break it.
  const runAt = buildRunAt(spec);
  if (!runAt || !now) { return false; }
  return runAt < now;                 // both 'YYYY-MM-DDTHH:MM', so lexical order is chronological
}

// 'YYYY-MM-DD' as "12 August 2026". Written out rather than left numeric because 12/08 and 08/12
// are the same string to two different readers.
function longDate(date: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec((date || '').trim());
  if (!match) { return date || ''; }
  const months = ['January', 'February', 'March', 'April', 'May', 'June',
                  'July', 'August', 'September', 'October', 'November', 'December'];
  return `${Number(match[3])} ${months[Number(match[2]) - 1]} ${match[1]}`;
}

// --- ion-datetime interop ------------------------------------------------------------------------
// The pickers are ion-datetime in an ion-popover.
//
// Two other options were tried and rejected. A native <input type="date"> drags the BROWSER's
// calendar panel along, which no stylesheet in this app can reach. kendo-datepicker - the same
// component reporting and dashboard use - compiles and renders here but never opens its popup
// inside an ion-modal; those two pages are not inside an overlay, which is why they are fine.
// Ionic's own overlay cannot hit that class of problem, because Ionic manages the layer.
//
// ion-datetime speaks ISO-8601 and is loose about how much of it it returns, so both ends are
// normalised by string rather than parsed - a Date round trip through new Date('2026-08-12')
// would read UTC midnight and come back as the day before anywhere west of Greenwich.

// 'YYYY-MM-DD' out of anything ISO-shaped, or '' when there is no date in it.
export function isoDateOf(value: any): string {
  const match = /(\d{4})-(\d{2})-(\d{2})/.exec(String(value ?? ''));
  return match ? match[0] : '';
}

// 'HH:MM' out of anything ISO-shaped, or '' when there is no time in it. Anchored to the T so a
// bare date cannot have its month and day read as an hour and a minute.
export function isoTimeOf(value: any): string {
  const text = String(value ?? '');
  const match = /T(\d{2}):(\d{2})/.exec(text) || /^(\d{2}):(\d{2})$/.exec(text);
  return match ? `${match[1]}:${match[2]}` : '';
}

// ion-datetime will not parse a bare 'HH:MM' for a time presentation. The date part is filler and
// isoTimeOf throws it away again on the way back.
export function timeToIso(time: string): string {
  return parseTime(time) ? `2000-01-01T${time}:00` : '';
}

// 'YYYY-MM-DD' as "12 August 2026", for the button that opens the picker.
export function formatDateLong(date: string): string {
  return longDate(date);
}

// What the time box should show after a keystroke - applied on every input, so the field can only
// ever hold something time-shaped. Without it "2222222:99999" is a thing you can type and only
// find out about on blur.
//
// It corrects rather than merely blocks, which is what makes it feel like a normal hour field:
//   - anything that is not a digit is dropped, and the colon is inserted by the mask
//   - a first digit above 2 cannot start an hour, so it becomes the hour: "9" -> "09:"
//   - a digit that would make the hour or the minutes impossible is refused, so "9" then "9" in
//     the hour leaves "09:" rather than "99"
// The blur parser still runs afterwards; this only stops nonsense being typeable in the meantime.
export function maskTimeInput(text: string): string {
  let digits = (text || '').replace(/\D/g, '').slice(0, 4);
  if (!digits) { return ''; }

  // 3-9 cannot be the tens of an hour, so it is the hour itself and the rest are minutes.
  if (digits.length === 1 && digits > '2') { digits = `0${digits}`; }

  // Refuse the second hour digit rather than clamping: clamping would silently turn a typo into a
  // real time, and 23:00 is a worse thing to schedule by accident than an obviously unfinished box.
  if (digits.length >= 2 && Number(digits.slice(0, 2)) > 23) { digits = digits.slice(0, 1); }
  // Same for the tens of the minutes - 6-9 cannot start one.
  if (digits.length >= 3 && digits[2] > '5') { digits = digits.slice(0, 2); }

  return digits.length <= 2 ? digits : `${digits.slice(0, 2)}:${digits.slice(2)}`;
}

// What someone typed into the time box, as 'HH:MM', or '' when it cannot be read as a time.
//
// Typing is the fast path - the list only offers quarter hours, and 09:37 is a legitimate thing to
// want - so this accepts the shorthands people actually type rather than demanding the canonical
// form: "9" and "9:00" and "0900" all mean the same thing. Returning '' for the rest leaves
// scheduleError to say "Pick a time of day", which is already the message for a missing one.
export function normalizeTimeInput(text: string): string {
  const raw = (text || '').trim().replace(/\s/g, '');
  if (!raw) { return ''; }

  let hours: number;
  let minutes: number;

  const withSeparator = /^(\d{1,2})[:.](\d{1,2})$/.exec(raw);
  if (withSeparator) {
    hours = Number(withSeparator[1]);
    // "9:5" is five past, not fifty past - a single digit after the separator is a tens-place
    // typo far more often than it is shorthand for 50.
    minutes = Number(withSeparator[2].padStart(2, '0'));
  } else if (/^\d{1,4}$/.test(raw)) {
    // Bare digits: 1-2 of them are an hour, 3-4 are an hour and minutes. "930" is half nine.
    if (raw.length <= 2) { hours = Number(raw); minutes = 0; }
    else { hours = Number(raw.slice(0, raw.length - 2)); minutes = Number(raw.slice(-2)); }
  } else {
    return '';
  }

  if (!isFinite(hours) || !isFinite(minutes)) { return ''; }
  if (hours > 23 || minutes > 59) { return ''; }
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

// "Monday, Wednesday and Friday" - the Oxford-less list a sentence needs.
function joinWords(words: string[]): string {
  if (words.length <= 1) { return words[0] || ''; }
  return `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`;
}

function ordinal(day: number): string {
  // 11th, 12th and 13th are the exceptions the simple rule gets wrong.
  const teen = day % 100;
  if (teen >= 11 && teen <= 13) { return `${day}th`; }
  switch (day % 10) {
    case 1: return `${day}st`;
    case 2: return `${day}nd`;
    case 3: return `${day}rd`;
    default: return `${day}th`;
  }
}

// The inverse of buildCron / buildRunAt: a stored schedule back into the picker's state.
//
// This exists because the backend's schema stores `cron`, `run_at` and `timezone` and NOTHING else -
// there is no schedule_spec any more. Editing a schedule therefore means reading the expression back,
// and the only honest way to do that is to invert the small set of shapes buildCron can produce.
//
// Anything OUTSIDE that set returns null rather than a guess. A hand-written cron (say `*/15 * * * *`)
// has no representation in a frequency-and-time picker, and showing it as "every day at 00:00" would
// be a confident lie that overwrites the real schedule on the next save. The caller shows the raw
// expression instead and leaves it alone.
export function parseCron(cron: string, runAt?: string | null): ScheduleSpec | null {
  const once = parseRunAt(runAt);
  if (once) { return once; }
  const parts = (cron || '').trim().split(/\s+/);
  if (parts.length !== 5) { return null; }
  const [min, hour, dayOfMonth, month, weekday] = parts;
  if (!/^\d{1,2}$/.test(min) || !/^\d{1,2}$/.test(hour) || month !== '*') { return null; }
  const time = `${pad(hour)}:${pad(min)}`;
  const base: ScheduleSpec = { ...blankSchedule(), time };

  if (dayOfMonth === '*' && weekday === '*') { return { ...base, frequency: 'daily' }; }
  if (dayOfMonth === '*' && weekday === '1-5') { return { ...base, frequency: 'weekdays' }; }
  if (dayOfMonth === '*' && /^[0-6](,[0-6])*$/.test(weekday)) {
    const weekdays = weekday.split(',').map(Number).sort((a, b) => a - b);
    return { ...base, frequency: 'weekly', weekdays };
  }
  if (weekday === '*' && /^\d{1,2}$/.test(dayOfMonth)) {
    const day = Number(dayOfMonth);
    if (day >= 1 && day <= 31) { return { ...base, frequency: 'monthly', dayOfMonth: day }; }
  }
  return null;
}

// A one-time run, stored as the same 'YYYY-MM-DDTHH:MM' buildRunAt produces. Read before the cron so
// a document carrying both (which should not happen) is treated as the one-time it says it is.
function parseRunAt(runAt?: string | null): ScheduleSpec | null {
  const value = (runAt || '').trim();
  const match = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/.exec(value);
  if (!match) { return null; }
  return { ...blankSchedule(), frequency: 'once', date: match[1], time: match[2] };
}

function pad(value: string): string {
  return value.length < 2 ? `0${value}` : value;
}
